import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { CampaignSource, CampaignTypes } from 'features/campaigns';
import { PRIZE_POP_CAMPAIGN_TYPE, REBATE_CAMPAIGN_TYPE, TOURNAMENTS_CAMPAIGN_TYPE } from 'features/campaigns';
import type { CampaignPanelInfo } from '../../../components/MultiViewCampaignSection';
import { formatDayMonth } from 'shared/utils/dateHelpers';
import {
  useCampaignType,
  useCampaignsStore,
  usePanelInfo,
  useSelectedCampaign,
} from '../../../store/campaignsStore';

import { evaluateDefaultCampaignStatus } from './variants/default/evaluateDefaultCampaignStatus';
import { evaluatePrizePopCampaignStatus } from './variants/prizePop/evaluatePrizePopCampaignStatus';

type CampaignStatusResult = { isActive: boolean; isFinished: boolean; isPromo: boolean };

// Keyed exhaustively over CampaignTypes so a new campaign type fails to compile here until mapped.
const campaignStatusEvaluators: Record<CampaignTypes, (campaign: CampaignSource) => CampaignStatusResult> = {
  [PRIZE_POP_CAMPAIGN_TYPE]: evaluatePrizePopCampaignStatus,
  [TOURNAMENTS_CAMPAIGN_TYPE]: evaluateDefaultCampaignStatus,
  [REBATE_CAMPAIGN_TYPE]: evaluateDefaultCampaignStatus,
};

/**
 * Pure, non-hook access to the same per-type status evaluation `useCampaignStatus` uses
 * internally. Use this where the hook itself can't be called — e.g. outside a
 * `CampaignTypeProvider`, or per-item inside a loop/array method.
 */
export const evaluateCampaignStatus = (campaign: CampaignSource): CampaignStatusResult =>
  campaignStatusEvaluators[campaign.type](campaign);

// Get campaign by ID and type, or return selectedCampaign, or panelInfo
const getCampaignById = (
  campaignId: string | undefined,
  selectedCampaign: CampaignSource | undefined,
  campaigns: CampaignSource[],
  panelInfo: CampaignPanelInfo
) => {

  // This prevent collision when we have multiple campaigns with the same ID but different types
  if (campaignId && panelInfo.type) return campaigns.find(c => c.id === campaignId && c.type === panelInfo.type) || campaigns.find(c => c.id === campaignId);
  //Fallback to find by ID only if type match not found, to maintain backward compatibility with old panelInfo structure where type might be missing
  if (campaignId) return campaigns.find(c => c.id === campaignId);
  return selectedCampaign;
};

// Only update React state if value actually changed (prevents unnecessary re-renders)
const updateStateIfChanged = (
  newValue: boolean,
  prevRef: React.RefObject<boolean>,
  setState: (value: boolean) => void
) => {
  if (prevRef.current !== newValue) {
    prevRef.current = newValue;
    setState(newValue);
  }
};

/**
 * Lightweight hook for campaign state only.
 * Does NOT force re-renders every second — only re-renders when
 * isActiveSlot or campaign status actually changes.
 *
 * For displaying the ticking countdown, use <CountdownTimer /> instead.
 *
 * Delegates the actual "is active/finished/promo" evaluation to a
 * per-campaign-type variant (see ./variants), since tournaments and
 * prize pops have different timing models.
 */
export const useCampaignStatus = (campaignId?: string) => {
  const selectedCampaign = useSelectedCampaign();
  const campaigns = useCampaignsStore((state) => state.campaigns);
  const campaignType = useCampaignType();
  const panelInfo = usePanelInfo(campaignType);

  const campaign = useMemo(
    () => getCampaignById(campaignId, selectedCampaign, campaigns, panelInfo),
    [campaignId, selectedCampaign, campaigns, panelInfo]
  );

  const evaluateStatus = useCallback(() => {
    if (!campaign) return { isActive: false, isFinished: false, isPromo: false };
    return campaignStatusEvaluators[campaign.type](campaign);
  }, [campaign]);

  const initialStatus = useMemo(() => evaluateStatus(), [evaluateStatus]);

  const [isActiveSlot, setIsActiveSlot] = useState(initialStatus.isActive);
  const [isFinished, setIsFinished] = useState(initialStatus.isFinished);
  const [isPromo, setIsPromo] = useState(initialStatus.isPromo);

  const prevIsActiveRef = useRef(isActiveSlot);
  const prevIsFinishedRef = useRef(isFinished);
  const prevIsPromoRef = useRef(isPromo);

  const checkAndUpdateStatus = useCallback(() => {
    const { isActive, isFinished, isPromo } = evaluateStatus();

    updateStateIfChanged(isActive, prevIsActiveRef, setIsActiveSlot);
    updateStateIfChanged(isFinished, prevIsFinishedRef, setIsFinished);
    updateStateIfChanged(isPromo, prevIsPromoRef, setIsPromo);
  }, [evaluateStatus]);

  useEffect(() => {
    checkAndUpdateStatus();

    const intervalId = setInterval(checkAndUpdateStatus, 1000);

    return () => clearInterval(intervalId);
  }, [checkAndUpdateStatus]);

  // Static timing info (dates) - only recalculates when campaign changes
  const staticTiming = useMemo(() => {
    const startDate = campaign && 'startDate' in campaign ? campaign.startDate : undefined;
    const endDate = campaign && 'endDate' in campaign ? campaign.endDate : undefined;

    return {
      startDate: startDate ? formatDayMonth(new Date(startDate)) : 'N/A',
      endDate: endDate ? formatDayMonth(new Date(endDate)) : 'N/A',
    };
  }, [campaign]);

  return {
    isActiveSlot,
    isFinished,
    isPromo,
    campaign,
    staticTiming,
  };
};
